import { ref, computed, onBeforeUnmount } from 'vue';
import type { 
  SlideCanvasBox, 
  SlideCanvasPoint, 
  SlideInteractionDependencies, 
  InteractionState,
  SlideRenderBox,
} from '../components/SlideCanvas.types';

import { 
  getCanvasPointRaw as getCanvasPointRawUtil, 
  toNaturalPoint,
  findInteractionTarget
} from '../utils/slideHitTesting';
import { getHandleCursor } from '../utils/slideInteraction';
import { applyMove } from '../utils/SlideMoveAction';
import { applyResize } from '../utils/SlideResizeAction';
import { initializeRotation, applyRotation } from '../utils/SlideRotateAction';
import type { RotationState } from '../utils/SlideRotateAction';
import { buildBox, isBoxDrawable } from '../utils/slideGeometry';

const DRAG_THRESHOLD = 5;

type SlideInteractionState = InteractionState & {
  targetId?: string;
  targetLabel?: string;
};

function createIdleInteraction(): SlideInteractionState {
  return {
    type: 'none',
    handle: null,
    startPoint: { x: 0, y: 0 },
    baseBox: { x: 0, y: 0, width: 0, height: 0, rotation: 0 },
    pendingAction: null,
    targetId: undefined,
    targetLabel: undefined,
  };
}

export function useSlideInteraction(deps: SlideInteractionDependencies) {
  const { 
    scalingContext, 
    interactive, 
    selectedRenderBox, 
    slideRegions, 
    onDrawStart, 
    onDrawEnd, 
    onRegionChange, 
    scheduleDraw,
    getDefaultLabel
  } = deps;

  const { renderBoxes, selectAtPoint, addBox, updateBoxGeometry, updateBoxRotation } = slideRegions;

  // --- Internal State ---
  const draftBox = ref<SlideCanvasBox | null>(null);
  const hoverPoint = ref<SlideCanvasPoint | null>(null);
  const rotationState = ref<RotationState | null>(null);

  const interaction = ref<SlideInteractionState>(createIdleInteraction());

  // --- Helpers ---
  function getCanvasPointRaw(event: PointerEvent | MouseEvent, canvas: HTMLCanvasElement): SlideCanvasPoint | null {
    const rect = canvas.getBoundingClientRect();
    return getCanvasPointRawUtil(
      event.clientX,
      event.clientY,
      { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      scalingContext.value.width,
      scalingContext.value.height,
    );
  }

  function getCanvasPoint(event: PointerEvent | MouseEvent, canvas: HTMLCanvasElement): SlideCanvasPoint | null {
    const raw = getCanvasPointRaw(event, canvas);
    if (!raw) return null;
    return toNaturalPoint(raw, scalingContext.value.scaleX, scalingContext.value.scaleY);
  }

  function stopInteraction() {
    interaction.value = createIdleInteraction();
    draftBox.value = null;
    rotationState.value = null;
  }

  function setPendingInteraction(params: {
    point: SlideCanvasPoint;
    box: SlideRenderBox;
    handle: InteractionState['handle'];
    pendingAction: NonNullable<InteractionState['pendingAction']>;
  }) {
    const { point, box, handle, pendingAction } = params;
    interaction.value = {
      type: 'pending',
      handle,
      startPoint: point,
      baseBox: { ...box },
      targetId: box.id,
      targetLabel: box.label,
      pendingAction,
    };
  }

  function startDrawing(point: SlideCanvasPoint) {
    interaction.value = {
      type: 'drawing',
      handle: null,
      startPoint: point,
      baseBox: { x: point.x, y: point.y, width: 0, height: 0, rotation: 0 },
      pendingAction: null,
    };
    draftBox.value = { ...interaction.value.baseBox };
    onDrawStart(point);
  }

  function startRotating(point: SlideCanvasPoint, pointCanvas: SlideCanvasPoint, box: SlideRenderBox) {
    rotationState.value = initializeRotation(box, pointCanvas, scalingContext.value.scaleX, scalingContext.value.scaleY);
    interaction.value = {
      type: 'rotating',
      handle: null,
      startPoint: point,
      baseBox: { ...box },
      targetId: box.id,
      targetLabel: box.label,
    };
  }

  function resolveInteractionTarget(point: SlideCanvasPoint, pointCanvas: SlideCanvasPoint) {
    return findInteractionTarget({
      pointNatural: point,
      pointCanvas,
      selectedBox: selectedRenderBox.value,
      allBoxes: renderBoxes.value,
      ...scalingContext.value,
    });
  }

  function promotePendingInteraction(visualDistance: number) {
    if (interaction.value.type !== 'pending' || !interaction.value.pendingAction) return;

    if (visualDistance < DRAG_THRESHOLD) return;

    interaction.value.type = interaction.value.pendingAction === 'resize' ? 'resizing' : 'moving';
    interaction.value.pendingAction = null;
  }

  function handleRotatingMove(event: PointerEvent, pointCanvas: SlideCanvasPoint) {
    if (interaction.value.type !== 'rotating' || !rotationState.value || !interaction.value.targetId) {
      return false;
    }

    event.preventDefault();
    const nextRotation = applyRotation(pointCanvas, rotationState.value);
    
    updateBoxRotation(interaction.value.targetId, interaction.value.targetLabel!, nextRotation);
    scheduleDraw();
    return true;
  }



  function handleDrawingMove(event: PointerEvent, point: SlideCanvasPoint) {
    if (interaction.value.type !== 'drawing') return false;

    event.preventDefault();
    draftBox.value = buildBox(
      interaction.value.startPoint,
      point,
      scalingContext.value.naturalMaxWidth,
      scalingContext.value.naturalMaxHeight,
    );
    scheduleDraw();
    return true;
  }

  function handleMovingMove(point: SlideCanvasPoint) {
    if (interaction.value.type !== 'moving' || !interaction.value.targetId) return false;

    const nextPos = applyMove(
      interaction.value.baseBox,
      interaction.value.startPoint,
      point
    );
    updateBoxGeometry(
      interaction.value.targetId,
      interaction.value.targetLabel!,
      { ...interaction.value.baseBox, ...nextPos },
    );
    scheduleDraw();
    return true;
  }

  function handleResizingMove(event: PointerEvent, point: SlideCanvasPoint) {
    if (interaction.value.type !== 'resizing' || !interaction.value.targetId) return false;

    event.preventDefault();
    const nextGeometry = applyResize(
      interaction.value.baseBox, 
      interaction.value.handle, 
      point
    );

    // Update geometry directly (allowing corners to go off-image)
    updateBoxGeometry(interaction.value.targetId, interaction.value.targetLabel!, nextGeometry);
    
    scheduleDraw();
    return true;
  }

  function completeDrawingInteraction(event: PointerEvent, canvas: HTMLCanvasElement) {
    const point = getCanvasPoint(event, canvas) ?? interaction.value.startPoint;
    finalizeDraft(point, getDefaultLabel());
  }

  function completeNonDrawingInteraction(previousInteractionType: InteractionState['type']) {
    const shouldEmitRegionChange =
      previousInteractionType === 'moving' ||
      previousInteractionType === 'resizing' ||
      previousInteractionType === 'rotating';

    stopInteraction();
    if (shouldEmitRegionChange) {
      deps.onRegionChange();
    }
  }

  function resolveHoverCursor() {
    if (!hoverPoint.value) return 'crosshair';

    const pointCanvas = {
      x: hoverPoint.value.x * scalingContext.value.scaleX,
      y: hoverPoint.value.y * scalingContext.value.scaleY,
    };
    const target = resolveInteractionTarget(hoverPoint.value, pointCanvas);

    if (target.type === 'resize-handle' && target.box) {
      return getHandleCursor(target.handle!, target.box.rotation);
    }
    if (target.type === 'rotation-handle') return 'grab';
    if (target.type === 'interior') return 'move';
    return 'crosshair';
  }

  function finalizeDraft(point: SlideCanvasPoint, defaultLabel: string) {
    if (interaction.value.type !== 'drawing') {
      stopInteraction();
      return;
    }
    const box = buildBox(interaction.value.startPoint, point, scalingContext.value.naturalMaxWidth, scalingContext.value.naturalMaxHeight);
    stopInteraction();

    if (!isBoxDrawable(box, 4, scalingContext.value.scaleX, scalingContext.value.scaleY)) {
      scheduleDraw();
      return;
    }

    const persisted = addBox(box, defaultLabel);
    onDrawEnd({
      id: persisted.id,
      x: persisted.x,
      y: persisted.y,
      width: persisted.width,
      height: persisted.height,
      rotation: persisted.rotation,
    });
    onRegionChange();
    scheduleDraw();
  }

  // --- Handlers ---
  function handlePointerDown(event: PointerEvent) {
    const canvas = event.currentTarget as HTMLCanvasElement;
    const isPrimaryPointer = event.button === 0 || event.button === -1 || event.pointerType === 'touch';
    if (!isPrimaryPointer) return;

    // CAPTURE: Directs all subsequent events to this canvas until up/cancel
    canvas.setPointerCapture(event.pointerId);

    const pointCanvas = getCanvasPointRaw(event, canvas);
    if (!pointCanvas) return;
    const point = toNaturalPoint(pointCanvas, scalingContext.value.scaleX, scalingContext.value.scaleY);

    event.preventDefault();
    canvas.focus();
    if (interaction.value.type !== 'none') return;

    const target = resolveInteractionTarget(point, pointCanvas);

    if (target.type === 'rotation-handle' && target.box) {
      startRotating(point, pointCanvas, target.box);
      return;
    }

    if (target.type === 'resize-handle' && target.box) {
      setPendingInteraction({
        point,
        box: target.box,
        handle: target.handle || null,
        pendingAction: 'resize',
      });
      return;
    }

    if (target.type === 'interior' && target.box) {
      selectAtPoint(point);
      setPendingInteraction({
        point,
        box: target.box,
        handle: null,
        pendingAction: 'move',
      });
      scheduleDraw();
      return;
    }

    if (!interactive.value) {
      selectAtPoint(point);
      scheduleDraw();
      return;
    }

    if (target.type !== 'background') return;

    const hit = selectAtPoint(point);
    if (!hit) {
      startDrawing(point);
    }
    scheduleDraw();
  }

  function handlePointerMove(event: PointerEvent) {
    const canvas = event.target as HTMLCanvasElement;
    const pointCanvas = getCanvasPointRaw(event, canvas);
    if (!pointCanvas) return;
    const point = toNaturalPoint(pointCanvas, scalingContext.value.scaleX, scalingContext.value.scaleY);
    hoverPoint.value = point;

    if (interaction.value.type === 'pending') {
      const dx = (point.x - interaction.value.startPoint.x) * scalingContext.value.scaleX;
      const dy = (point.y - interaction.value.startPoint.y) * scalingContext.value.scaleY;
      const visualDistance = Math.sqrt(dx * dx + dy * dy);
      promotePendingInteraction(visualDistance);
    }

    if (handleRotatingMove(event, pointCanvas)) return;
    if (handleDrawingMove(event, point)) return;
    if (handleMovingMove(point)) return;
    handleResizingMove(event, point);
  }

  function handlePointerUp(event: PointerEvent) {
    const canvas = event.target as HTMLCanvasElement;
    const previousInteractionType = interaction.value.type;

    if (previousInteractionType === 'drawing') {
      completeDrawingInteraction(event, canvas);
    } else {
      completeNonDrawingInteraction(previousInteractionType);
    }
    deps.scheduleDraw();
  }

  function handlePointerCancel() {
    stopInteraction();
    deps.scheduleDraw();
  }


  const canvasCursor = computed(() => {
    if (interaction.value.type === 'pending') {
      return interaction.value.pendingAction === 'resize'
        ? getHandleCursor(interaction.value.handle, interaction.value.baseBox.rotation)
        : 'move';
    }
    if (interaction.value.type === 'moving') return 'move';
    if (interaction.value.type === 'rotating') return 'grabbing';
    if (interaction.value.type === 'resizing') return getHandleCursor(interaction.value.handle, interaction.value.baseBox.rotation);
    if (!interactive.value) return 'default';

    return resolveHoverCursor();
  });

  onBeforeUnmount(stopInteraction);

  return {
    interaction,
    draftBox,
    hoverPoint,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    stopInteraction,
    canvasCursor,
  };
}
